import React, { useState } from "react";
import { formatBRDate } from "../utils/date";
import { HonorCertificate } from "./HonorCertificate";
import { generateHonorCommunique, detectMasonicMilestones } from "./HonorCommuniquePDF";
import { toast } from "sonner";
import { 
  Search, 
  UserPlus, 
  Phone, 
  Mail, 
  Compass, 
  Calendar, 
  Coins, 
  Trash2, 
  Edit3, 
  ShieldCheck, 
  CheckCircle2, 
  XCircle,
  Award,
  Sparkles,
  Crown,
  FileText,
  ScrollText,
} from "lucide-react";

export const MembersDirectory = ({
  members,
  lodgeInfo,
  onOpenNewMemberModal,
  onOpenEditMemberModal,
  onDeleteMember,
  onViewMemberStatement,
  canManage = true, // Secretaria/Master. Se false, esconde criar/editar/excluir/certificado/extrato
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [degreeFilter, setDegreeFilter] = useState("all");
  const [certificateMember, setCertificateMember] = useState(null);

  const filteredMembers = members.filter((m) => {
    const s = searchTerm.toLowerCase();
    const matchText = (
      m.name.toLowerCase().includes(s) ||
      m.cim.includes(s) ||
      m.office.toLowerCase().includes(s) ||
      m.email.toLowerCase().includes(s)
    );
    let matchDegree = true;
    if (degreeFilter === "honor:emerito") matchDegree = m.honor_title === "emerito";
    else if (degreeFilter === "honor:provecto") matchDegree = m.honor_title === "provecto";
    else matchDegree = degreeFilter === "all" || m.degree === degreeFilter;
    return matchText && matchDegree;
  });

  const emeritoCount = members.filter((m) => m.honor_title === "emerito").length;
  const provectoCount = members.filter((m) => m.honor_title === "provecto").length;

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  };

  const getDegreeBadgeClass = (degree) => {
    switch (degree) {
      case "1 - Aprendiz":
        return "bg-sky-500/15 text-sky-400 border-sky-500/30";
      case "2 - Companheiro":
        return "bg-purple-500/15 text-purple-400 border-purple-500/30";
      case "3 - Mestre":
        return "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40";
      case "Mestre Instalado":
        return "bg-amber-500/20 text-amber-300 border-amber-500/40";
      default:
        return "bg-slate-700 text-slate-300";
    }
  };

  return (
    <div className="space-y-6" data-testid="members-directory-section">
      
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif font-bold text-white">Quadro de Obreiros</h2>
          <p className="text-xs text-slate-400">
            Registro dos Irmãos do Quadro, Graus, Cargos em Loja e Frequência Acumulada
          </p>
        </div>

        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="input-search-members"
              type="text"
              placeholder="Buscar por nome, CIM ou cargo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#131C2E] text-xs text-white placeholder-slate-500 pl-9 pr-3 py-2.5 rounded-xl border border-slate-700 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {canManage && (
            <button
              data-testid="btn-add-member-main"
              onClick={onOpenNewMemberModal}
              className="flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)] shrink-0"
            >
              <UserPlus className="w-4 h-4" />
              <span>Novo Irmão</span>
            </button>
          )}
        </div>
      </div>

      {/* Degree Filters Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { id: "all", label: "Todos os Obreiros" },
          { id: "1 - Aprendiz", label: "Aprendizes (1º)" },
          { id: "2 - Companheiro", label: "Companheiros (2º)" },
          { id: "3 - Mestre", label: "Mestres (3º)" },
          { id: "Mestre Instalado", label: "Mestres Instalados" }
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`filter-degree-${tab.id}`}
            onClick={() => setDegreeFilter(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              degreeFilter === tab.id
                ? "bg-[#D4AF37] text-slate-950 shadow-[0_0_10px_rgba(212,175,55,0.3)]"
                : "bg-[#131C2E] text-slate-400 hover:text-white border border-slate-800"
            }`}
          >
            {tab.label}
          </button>
        ))}
        {emeritoCount > 0 && (
          <button
            data-testid="filter-honor-emerito"
            onClick={() => setDegreeFilter("honor:emerito")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
              degreeFilter === "honor:emerito"
                ? "bg-[#D4AF37] text-slate-950 shadow-[0_0_10px_rgba(212,175,55,0.3)]"
                : "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/20"
            }`}
          >
            <Crown className="w-3 h-3" />
            Eméritos ({emeritoCount})
          </button>
        )}
        {provectoCount > 0 && (
          <button
            data-testid="filter-honor-provecto"
            onClick={() => setDegreeFilter("honor:provecto")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1 ${
              degreeFilter === "honor:provecto"
                ? "bg-emerald-400 text-slate-950 shadow-[0_0_10px_rgba(52,211,153,0.3)]"
                : "bg-emerald-500/10 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/20"
            }`}
          >
            <Crown className="w-3 h-3" />
            Provectos ({provectoCount})
          </button>
        )}
      </div>

      {/* Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {filteredMembers.map((member) => {
          const rawPct = member.attendance?.percentage;
          const hasAttendance = rawPct !== null && rawPct !== undefined;
          const attendancePct = hasAttendance ? rawPct : 0;
          const isHighAttendance = hasAttendance && attendancePct >= 75;
          const totalSess = member.attendance?.total_sessions || 0;
          const awaitingSessions = !hasAttendance || totalSess === 0;

          return (
            <div
              key={member.id}
              data-testid={`member-card-${member.id}`}
              className="luxury-card p-5 group hover:border-[#D4AF37]/60 transition-all flex flex-col justify-between"
            >
              <div>
                {/* Header of Card */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37]/30 to-[#131C2E] border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] overflow-hidden shrink-0">
                      {member.photo_b64 ? (
                        <img src={member.photo_b64} alt={member.name} className="w-full h-full object-cover" />
                      ) : (
                        <Compass className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-white group-hover:text-[#D4AF37] transition-colors leading-tight">
                        {member.name}
                      </h3>
                      <div className="flex items-center space-x-2 text-xs text-slate-400 mt-0.5">
                        <span className="font-mono text-[#D4AF37]">CIM: {member.cim}</span>
                        <span>•</span>
                        <span className="text-slate-300 font-medium">{member.office}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Degree & Regularity Badges */}
                <div className="flex items-center space-x-2 mt-3 mb-4">
                  <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${getDegreeBadgeClass(member.degree)}`}>
                    {member.degree}
                  </span>

                  <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full border ${
                    member.status === "regular"
                      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                      : "bg-rose-500/10 text-rose-400 border-rose-500/30"
                  }`}>
                    {member.status === "regular" ? "Regular em Loja" : member.status}
                  </span>
                </div>

                {/* Contact & Masonic Dates */}
                <div className="space-y-1.5 text-xs text-slate-300 border-t border-slate-800 pt-3">
                  <div className="flex items-center space-x-2 text-slate-400">
                    <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="truncate">{member.email}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-400">
                    <Phone className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span>{member.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-400">
                    <Calendar className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span>Iniciação: {formatBRDate(member.initiation_date)}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-slate-400">
                    <Coins className="w-3.5 h-3.5 text-emerald-400" />
                    <span>Capitação Mensal: {formatCurrency(member.monthly_fee)}</span>
                  </div>
                </div>

                {/* Honorary Title Badge */}
                {member.honor_title === "emerito" && (
                  <div
                    data-testid={`badge-emerito-${member.id}`}
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl border border-[#D4AF37]/60 bg-gradient-to-r from-[#D4AF37]/20 via-[#B8860B]/10 to-[#D4AF37]/20 shadow-[0_0_18px_rgba(212,175,55,0.25)]"
                  >
                    <Crown className="w-4 h-4 text-[#D4AF37] shrink-0" />
                    <div className="flex-1 leading-tight">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[#D4AF37]">Maçom Emérito</div>
                      {member.honor_title_granted_at && (
                        <div className="text-[10px] text-slate-300 italic">
                          Concedido em {formatBRDate(member.honor_title_granted_at)}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`btn-certificate-${member.id}`}
                      onClick={() => setCertificateMember(member)}
                      title="Emitir certificado formal em PDF"
                      className="text-[9px] font-bold uppercase tracking-wider text-[#D4AF37] hover:text-white px-2 py-1 rounded border border-[#D4AF37]/50 hover:border-[#D4AF37] hover:bg-[#D4AF37]/20 transition-all shrink-0"
                    >
                      Certificado
                    </button>
                  </div>
                )}
                {member.honor_title === "provecto" && (
                  <div
                    data-testid={`badge-provecto-${member.id}`}
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-xl border border-emerald-400/60 bg-gradient-to-r from-emerald-500/20 via-emerald-600/10 to-emerald-500/20 shadow-[0_0_18px_rgba(52,211,153,0.25)]"
                  >
                    <Crown className="w-4 h-4 text-emerald-400 shrink-0" />
                    <div className="flex-1 leading-tight">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Maçom Provecto</div>
                      {member.honor_title_granted_at && (
                        <div className="text-[10px] text-slate-300 italic">
                          Concedido em {formatBRDate(member.honor_title_granted_at)}
                        </div>
                      )}
                    </div>
                    <button
                      data-testid={`btn-certificate-${member.id}`}
                      onClick={() => setCertificateMember(member)}
                      title="Emitir certificado formal em PDF"
                      className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 hover:text-white px-2 py-1 rounded border border-emerald-400/50 hover:border-emerald-400 hover:bg-emerald-500/20 transition-all shrink-0"
                    >
                      Certificado
                    </button>
                  </div>
                )}

                {/* Attendance Summary Bar */}
                <div className="mt-4 p-3 rounded-xl bg-[#080C14] border border-slate-800">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-slate-400 font-medium">Frequência em Sessões:</span>
                    {awaitingSessions ? (
                      <span className="font-mono font-bold text-amber-400 text-[10px] uppercase tracking-wider">
                        Aguardando 1ª sessão
                      </span>
                    ) : (
                      <span className={`font-mono font-bold ${isHighAttendance ? "text-emerald-400" : "text-rose-400"}`}>
                        {attendancePct}% ({member.attendance?.attended || 0}/{totalSess})
                      </span>
                    )}
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        awaitingSessions ? "bg-amber-500/40" : isHighAttendance ? "bg-[#D4AF37]" : "bg-rose-500"
                      }`}
                      style={{ width: `${awaitingSessions ? 0 : attendancePct}%` }}
                    />
                  </div>
                  {awaitingSessions && member.initiation_date && (
                    <div className="text-[10px] text-slate-500 mt-1.5 italic">
                      Contagem começa após a Iniciação — nenhuma sessão realizada ainda.
                    </div>
                  )}
                </div>

                {/* Regra Ouro — Progressão de Grau (denominador dinâmico) */}
                {member.frequency_rule && (() => {
                  const fr = member.frequency_rule;
                  const waiting = (fr.sessions_since || 0) === 0;
                  const badgeTone = fr.apto
                    ? { bg: "bg-emerald-500/5", brd: "border-emerald-500/40", chip: "bg-emerald-500/20 text-emerald-300 border-emerald-500/50", label: "APTO", bar: "bg-emerald-500" }
                    : waiting
                      ? { bg: "bg-amber-500/5", brd: "border-amber-500/40", chip: "bg-amber-500/20 text-amber-300 border-amber-500/50", label: "AGUARDANDO", bar: "bg-amber-500" }
                      : { bg: "bg-rose-500/5", brd: "border-rose-500/40", chip: "bg-rose-500/20 text-rose-300 border-rose-500/50", label: "NÃO APTO", bar: "bg-rose-500" };
                  return (
                  <div
                    data-testid={`freq-rule-${member.id}`}
                    className={`mt-2 p-3 rounded-xl border ${badgeTone.bg} ${badgeTone.brd}`}
                  >
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-slate-400 font-medium">
                        Regra Ouro • Próx: <span className="text-white font-semibold">{fr.next_grade}</span>
                      </span>
                      <span className={`font-bold text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${badgeTone.chip}`}>
                        {badgeTone.label}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] mb-1.5 text-slate-500">
                      <span>
                        {fr.attended}/{fr.sessions_since || 0} sessões
                        ({fr.percent}%)
                      </span>
                      <span className="italic">
                        desde {fr.anchor_label}
                      </span>
                    </div>
                    <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${badgeTone.bar}`}
                        style={{ width: `${waiting ? 0 : Math.min(100, fr.percent)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1.5 italic leading-tight">
                      {fr.msg}
                    </p>
                  </div>
                  );
                })()}

              </div>

              {/* Card Footer Actions — visíveis apenas para Secretaria/Master */}
              {canManage && (
              <div className="mt-5 pt-3 border-t border-slate-800 flex items-center justify-between">
                <button
                  data-testid={`btn-view-statement-${member.id}`}
                  onClick={() => onViewMemberStatement(member.id)}
                  className="text-xs text-[#D4AF37] hover:underline font-bold flex items-center space-x-1"
                >
                  <Coins className="w-3.5 h-3.5 text-[#D4AF37]" />
                  <span>Extrato Individual</span>
                </button>

                <div className="flex items-center space-x-2">
                  <button
                    data-testid={`btn-communique-member-${member.id}`}
                    onClick={() => {
                      const milestones = detectMasonicMilestones(member);
                      if (milestones.length === 0) {
                        // Sem marco detectado — gera comunicado genérico
                        generateHonorCommunique({
                          memberName: member.name,
                          cim: member.cim,
                          lodgeName: lodgeInfo?.name || "Loja Maçônica",
                          event: "Comunicado Fraterno",
                          eventDate: new Date().toISOString().slice(0, 10),
                          description: `A Loja registra e homenageia o Ir.·. ${member.name}, dedicado obreiro do Quadro, por sua trajetória e contribuição à Arte Real.`,
                          city: lodgeInfo?.city || "",
                          logoB64: lodgeInfo?.logo_b64 || null,
                        });
                      } else {
                        const m = milestones[0];
                        generateHonorCommunique({
                          memberName: member.name,
                          cim: member.cim,
                          lodgeName: lodgeInfo?.name || "Loja Maçônica",
                          event: m.event,
                          eventDate: m.date,
                          description: m.description,
                          city: lodgeInfo?.city || "",
                          logoB64: lodgeInfo?.logo_b64 || null,
                        });
                      }
                      toast.success("Comunicado gerado!");
                    }}
                    title="Emitir Comunicado (Diploma)"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  >
                    <ScrollText className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`btn-edit-member-${member.id}`}
                    onClick={() => onOpenEditMemberModal(member)}
                    title="Editar Irmão"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    data-testid={`btn-delete-member-${member.id}`}
                    onClick={() => onDeleteMember(member)}
                    title="Excluir Irmão"
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              )}

            </div>
          );
        })}
      </div>

      {filteredMembers.length === 0 && (
        <div className="luxury-card p-12 text-center">
          <Compass className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <h3 className="text-lg font-serif font-bold text-white mb-1">Nenhum Obreiro encontrado</h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
            Não há nenhum Irmão cadastrado com os critérios pesquisados.
          </p>
          {canManage && (
            <button
              onClick={onOpenNewMemberModal}
              className="bg-[#D4AF37] text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase"
            >
              Cadastrar Novo Irmão
            </button>
          )}
        </div>
      )}

      <HonorCertificate
        isOpen={!!certificateMember}
        member={certificateMember}
        lodgeInfo={lodgeInfo}
        onClose={() => setCertificateMember(null)}
      />

    </div>
  );
};
